import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Spinner,
  TextInput
} from "@patternfly/react-core";

import { apiGet, apiPost } from "../../app/api";
import { compactDateTime, formatNumber, readableEventLabel } from "../../display";
import { StandardDataTable, type StandardColumn } from "../../components/StandardDataTable";

type SyncEventRow = {
  id?: number;
  username?: string;
  full_name?: string;
  device_id?: string;
  action_path?: string;
  action_method?: string;
  synced_at?: string;
  status?: string;
  detail?: string;
};

type SyncConflictRow = {
  id?: number;
  username?: string;
  full_name?: string;
  document_type?: string;
  document_id?: string;
  action_path?: string;
  status?: string;
  conflict_type?: string;
  detail?: string;
  detected_at?: string;
};

type BackgroundJobRow = {
  id?: number;
  job_type?: string;
  label?: string;
  status?: string;
  created_by_name?: string;
  created_at?: string;
  completed_at?: string;
  error_message?: string;
};

type SyncStatusPayload = {
  summary?: {
    events?: number;
    synced?: number;
    needs_review?: number;
    resolved?: number;
    running_jobs?: number;
  };
  events?: SyncEventRow[];
  conflicts?: SyncConflictRow[];
  jobs?: BackgroundJobRow[];
};

function statusColor(status: string | undefined): "green" | "orange" | "red" | "blue" | "grey" {
  const clean = String(status || "").toLowerCase();
  if (clean.includes("failed") || clean.includes("review") || clean.includes("conflict")) return "red";
  if (clean.includes("running")) return "blue";
  if (clean.includes("completed") || clean.includes("synced") || clean.includes("resolved")) return "green";
  if (clean.includes("queued")) return "orange";
  return "grey";
}

export function SyncStatusWorkspace({ csrfToken }: { csrfToken: string }) {
  const [data, setData] = useState<SyncStatusPayload | null>(null);
  const [selectedConflict, setSelectedConflict] = useState<SyncConflictRow | null>(null);
  const [resolutionNote, setResolutionNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);

  const loadSyncStatus = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const payload = await apiGet<SyncStatusPayload>("/api/sync-status");
      setData(payload);
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Offline Uploads could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSyncStatus();
  }, [loadSyncStatus]);

  const resolveConflict = async (): Promise<void> => {
    if (!selectedConflict?.id) return;
    if (!resolutionNote.trim()) {
      setMessage({ variant: "danger", title: "Enter a resolution note before resolving the offline upload." });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await apiPost<{ ok: boolean }>("/api/sync-conflicts/resolve", {
        conflict_id: selectedConflict.id,
        resolution_note: resolutionNote.trim()
      }, csrfToken);
      setMessage({ variant: "success", title: "Offline upload marked resolved." });
      setSelectedConflict(null);
      setResolutionNote("");
      await loadSyncStatus();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Offline upload could not be resolved." });
    } finally {
      setSaving(false);
    }
  };

  const eventColumns: StandardColumn<SyncEventRow>[] = useMemo(() => [
    { key: "synced_at", label: "Synced", value: row => row.synced_at || "", render: row => compactDateTime(row.synced_at) },
    { key: "user", label: "User", value: row => row.full_name || row.username || "" },
    { key: "action_method", label: "Method", value: row => row.action_method || "" },
    { key: "action_path", label: "Endpoint", value: row => row.action_path || "" },
    { key: "status", label: "Status", value: row => row.status || "", render: row => <Label color={statusColor(row.status)}>{row.status || "Open"}</Label> },
    { key: "detail", label: "Detail", value: row => row.detail || "" }
  ], []);

  const conflictColumns: StandardColumn<SyncConflictRow>[] = useMemo(() => [
    { key: "detected_at", label: "Detected", value: row => row.detected_at || "", render: row => compactDateTime(row.detected_at) },
    { key: "user", label: "User", value: row => row.full_name || row.username || "" },
    { key: "document", label: "Document", value: row => `${row.document_type || ""} ${row.document_id || ""}` },
    { key: "conflict_type", label: "Issue", value: row => readableEventLabel(row.conflict_type) },
    { key: "status", label: "Status", value: row => row.status || "", render: row => <Label color={statusColor(row.status)}>{row.status || "Needs Review"}</Label> },
    { key: "detail", label: "Detail", value: row => row.detail || "" },
    { key: "actions", label: "Actions", sortable: false, render: row => <Button variant="link" isInline onClick={() => setSelectedConflict(row)}>Resolve</Button> }
  ], []);

  const jobColumns: StandardColumn<BackgroundJobRow>[] = useMemo(() => [
    { key: "created_at", label: "Created", value: row => row.created_at || "", render: row => compactDateTime(row.created_at) },
    { key: "label", label: "Job", value: row => row.label || row.job_type || "" },
    { key: "status", label: "Status", value: row => row.status || "", render: row => <Label color={statusColor(row.status)}>{row.status || "Open"}</Label> },
    { key: "created_by_name", label: "Started By", value: row => row.created_by_name || "" },
    { key: "completed_at", label: "Completed", value: row => row.completed_at || "", render: row => compactDateTime(row.completed_at) },
    { key: "error_message", label: "Error", value: row => row.error_message || "" }
  ], []);

  const summary = data?.summary || {};

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-sync-status-view">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
            <h1>Offline Uploads</h1>
            <p>Review device uploads, conflicts, and background import jobs.</p>
          </FlexItem>
          <FlexItem>
            <Button variant="secondary" onClick={() => void loadSyncStatus()} isDisabled={loading}>Refresh</Button>
          </FlexItem>
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        {loading ? <Spinner size="lg" aria-label="Loading Offline Uploads" /> : (
          <div className="pf-toj-stacked-work">
            <div className="pf-toj-status-grid pf-toj-compact-status-grid" aria-label="Offline upload status">
              <Card className="pf-toj-status-card"><CardBody><span>Events</span><strong>{formatNumber(summary.events || 0)}</strong></CardBody></Card>
              <Card className="pf-toj-status-card pf-toj-status-success"><CardBody><span>Synced</span><strong>{formatNumber(summary.synced || 0)}</strong></CardBody></Card>
              <Card className="pf-toj-status-card pf-toj-status-danger"><CardBody><span>Needs Review</span><strong>{formatNumber(summary.needs_review || 0)}</strong></CardBody></Card>
              <Card className="pf-toj-status-card"><CardBody><span>Resolved</span><strong>{formatNumber(summary.resolved || 0)}</strong></CardBody></Card>
              <Card className="pf-toj-status-card pf-toj-status-warning"><CardBody><span>Running Jobs</span><strong>{formatNumber(summary.running_jobs || 0)}</strong></CardBody></Card>
            </div>
            <Card className="pf-toj-work-card pf-toj-primary-work-card">
              <CardBody>
                <h2>Uploads Needing Review</h2>
                <StandardDataTable ariaLabel="Offline upload conflicts" rows={data?.conflicts || []} columns={conflictColumns} searchPlaceholder="Search offline upload conflicts" />
              </CardBody>
            </Card>
            <Card className="pf-toj-work-card">
              <CardBody>
                <h2>Recent Upload Events</h2>
                <StandardDataTable ariaLabel="Offline upload events" rows={data?.events || []} columns={eventColumns} searchPlaceholder="Search upload events" />
              </CardBody>
            </Card>
            <Card className="pf-toj-work-card">
              <CardBody>
                <h2>Background Jobs</h2>
                <StandardDataTable ariaLabel="Background jobs" rows={data?.jobs || []} columns={jobColumns} searchPlaceholder="Search background jobs" />
              </CardBody>
            </Card>
          </div>
        )}
      </div>
      <Modal variant={ModalVariant.small} isOpen={Boolean(selectedConflict)} onClose={() => setSelectedConflict(null)}>
        <ModalHeader title="Resolve offline upload" />
        <ModalBody>
          <Form>
            <FormGroup label="Resolution note" isRequired>
              <TextInput value={resolutionNote} onChange={(_event, value) => setResolutionNote(String(value))} aria-label="Resolution note" />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => void resolveConflict()} isLoading={saving}>Resolve</Button>
          <Button variant="secondary" onClick={() => setSelectedConflict(null)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </PageSection>
  );
}
